import React, { useEffect, useMemo } from 'react';
import { useForm, z, zodResolver } from '@fiverr-private/forms';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Gql } from '@fiverr-private/dynamic_matching';
import { appTranslate } from '../../../i18n';
import { centsToCurrency } from '../Budget/formatCurrency';
import { EditActions } from '../EditActions';
import { useBriefCreationContext } from '../../../context/BriefCreationContext';
import { AdditionalWorkCheckbox } from '../NeedType/AdditionalWorkCheckbox';
import { HourlyRateInput } from './Budget';
import { getBudgetInCents } from './BudgetAndTimelineSection';
import {
  BudgetAndTimelineContent,
  useBudgetAndTimelineContext,
  hasOneTimeHourlyShape,
  hasOngoingHourlyShape,
  useSetOngoingTimeline,
  getCommaFormatedStringAsNumber,
} from './utils';
import { DeliveryTimelineSelector, HiringDurationSelector, TimeCommitmentSelector } from './Timeline';

export const HourlyRateEditComponent = () => {
  const { currency } = useBriefCreationContext();
  const {
    sectionContentState,
    setSectionContentState,
    validMinBudget,
    validMaxBudget,
    needType,
    onEditEnd,
    onCancel,
    ongoingWorkPotential,
    setOngoingWorkPotential,
    additionalWorkPotentialAvailable,
  } = useBudgetAndTimelineContext();
  const { hourlyBudgetRange } = sectionContentState;

  useEffect(() => {
    const run = async () => {
      const minBudgetValue = hourlyBudgetRange?.min ? (hourlyBudgetRange.min / 100).toString() : '';

      const maxBudgetValue = hourlyBudgetRange?.max ? (hourlyBudgetRange.max / 100).toString() : '';

      methods.reset({ budget: { min: minBudgetValue, max: maxBudgetValue } });
      methods.clearErrors();
      await methods.trigger();
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionContentState.pricingType]);

  useSetOngoingTimeline({ sectionContentState, setSectionContentState, needType });

  const schema = useMemo(
    () =>
      z.object({
        budget: z
          .object({
            min: z.string().refine((value) => value === '' || getCommaFormatedStringAsNumber(value) > 0),
            max: z.string().refine((value) => value === '' || getCommaFormatedStringAsNumber(value) > 0),
          })
          .refine(
            (value: { min: string; max: string }) => {
              if (value.min === '' || value.max === '') {
                return true;
              }
              const minBudgetInCents = getBudgetInCents(value.min, currency);
              const maxBudgetInCents = getBudgetInCents(value.max, currency);

              return validMinBudget <= minBudgetInCents && maxBudgetInCents <= validMaxBudget;
            },
            {
              message: appTranslate(`sections.budget_and_timeline.invalid_hourly_rate`, {
                params: {
                  min: centsToCurrency(validMinBudget, currency),
                  max: centsToCurrency(validMaxBudget, currency),
                },
              }).toString(),
            }
          )
          .refine(
            (value: { min: string; max: string }) =>
              value.min === '' ||
              value.max === '' ||
              getBudgetInCents(value.max, currency) > getBudgetInCents(value.min, currency),
            {
              message: appTranslate(`sections.budget_and_timeline.invalid_range`).toString(),
            }
          ),
      }),
    [validMinBudget, validMaxBudget, currency]
  );

  const methods = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  });

  const validateAndChange = async (updatedValue: BudgetAndTimelineContent) => {
    if (await methods.trigger('budget')) {
      setSectionContentState(updatedValue);
    }
  };

  const isDisabled = () => {
    const { min, max } = hourlyBudgetRange ?? {};

    const hasValidBudget = !!min && !!max && min >= validMinBudget && max <= validMaxBudget && min < max;
    const sectionContentHasValidShape =
      needType === Gql.DmBriefNeedType.OneTime
        ? hasOneTimeHourlyShape(sectionContentState)
        : hasOngoingHourlyShape(sectionContentState);

    return !hasValidBudget || !sectionContentHasValidShape;
  };

  return (
    <Stack direction="column" gap="12" width="100%">
      <Stack direction="column" gap="2">
        <Stack>
          <HourlyRateInput validateAndChange={validateAndChange} methods={methods} />
        </Stack>
      </Stack>
      {needType === Gql.DmBriefNeedType.OneTime ? (
        <Stack>
          <DeliveryTimelineSelector />
        </Stack>
      ) : (
        <>
          <Stack>
            <TimeCommitmentSelector />
          </Stack>
          <Stack>
            <HiringDurationSelector />
          </Stack>
        </>
      )}
      <Container>
        {additionalWorkPotentialAvailable && (
          <Container marginBottom="6">
            <AdditionalWorkCheckbox checked={ongoingWorkPotential} onChange={setOngoingWorkPotential} />
          </Container>
        )}
        <EditActions onCancel={onCancel} onEditEnd={onEditEnd} isSaveDisabled={isDisabled()} />
      </Container>
    </Stack>
  );
};
